/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { useCallback, useState } from "react";
import { CosmosEnumHeroAlphaVariants, HeroAlphaFragment } from "@cosmos/hygraph-schema";

import { Animate, ResponsiveContainer, SectionContainer } from "@components/elements";
import { styled } from "@panda/jsx";
import { vstack } from "@panda/patterns";

import { HeroAlphaBlurredImage } from "./HeroAlpha.BlurredImage";
import { HeroAlphaContent } from "./HeroAlpha.Content";
import { HeroAlphaFullWidthImage } from "./HeroAlpha.FullWidthImage";
import { css } from "@panda/css";

const Styled = styled("main", {
	base: {
		display: "block",
		width: "100%",
	},
});

export const HeroAlpha = (props: HeroAlphaFragment | null): React.JSX.Element => {
	const isFullWidth = props?.variant === CosmosEnumHeroAlphaVariants.Fullwidth;
	const isBlurred = !isFullWidth; //default
	const blurredImageAsset = props?.blurredImage;
	const heroAsset = props?.heroAsset;
	// Treat blurredImage as "not provided" if it points at the same asset as heroAsset
	// to avoid fetching the same media twice (one with CORS, one without).
	const hasDistinctBlurredImage = Boolean(blurredImageAsset?.url) && blurredImageAsset?.url !== heroAsset?.url;
	const needsSharedSource = isBlurred && !hasDistinctBlurredImage;
	const [mediaElement, setMediaElement] = useState<HTMLImageElement | HTMLVideoElement | null>(null);

	const handleMediaRef = useCallback((el: HTMLImageElement | HTMLVideoElement | null) => {
		setMediaElement(el);
	}, []);

	const content = (
		<HeroAlphaContent
			isBlurred={isBlurred}
			heroAsset={props?.heroAsset}
			logoImage={props?.logoImage}
			logoVariant={props?.logoVariant}
			eyebrow={props?.eyebrow}
			title={props?.title}
			desc={props?.desc}
			buttons={props?.buttons || []}
			modButtons={props?.modButtons || []}
			gradient={props?.gradient}
			mediaRef={needsSharedSource ? handleMediaRef : undefined}
			crossOrigin={needsSharedSource ? "anonymous" : undefined}
		/>
	);

	const blurredLayout = (
		<ResponsiveContainer>
			<SectionContainer contentVisibility="unset" h="full">
				<section
					className={css({
						position: "relative",
						width: "1440px",
						h: "full",
						maxWidth: "full",
						maxHeight: "550px",
						mdDown: {
							maxHeight: "full",
						},
					})}
				>
					<Animate delay={0.35} type="fade">
						<HeroAlphaBlurredImage
							image={hasDistinctBlurredImage ? blurredImageAsset : heroAsset}
							width={977}
							height={550}
							blurPixels={80}
							sourceElement={needsSharedSource ? mediaElement : undefined}
						/>
					</Animate>
					{content}
				</section>
			</SectionContainer>
		</ResponsiveContainer>
	);

	const fullWidthLayout = (
		<>
			<HeroAlphaFullWidthImage heroAsset={props?.heroAsset} />
			<ResponsiveContainer>
				<SectionContainer overflowX="clip" contentVisibility="unset" h="100vh">
					{content}
				</SectionContainer>
			</ResponsiveContainer>
		</>
	);

	return (
		<Styled className={`hero-alpha ${vstack({ gap: "12" })}`}>{isBlurred ? blurredLayout : fullWidthLayout}</Styled>
	);
};
